<script setup lang="ts">
import { Link, router } from "@inertiajs/vue3"
import { reactive, ref } from "vue"
import { EyeIcon } from "@heroicons/vue/24/outline"
import AppShell from "../Layout/AppShell.vue"
import { UiButton, UiDataTable, UiDrawer } from "@/components"
import { useConfirmation } from "@/composables/useConfirmation"
import { useNotifications } from "@/composables/useNotifications"
import { formatCnpj, formatCpf, formatPhone, formatZipCode } from "@/lib/masks"
import type { UiDataTableColumn, UiDataTableFilter } from "@/types/ui"

type CustomerAddress = {
  id: number
  label: string
  street: string
  number: string
  complement: string
  district: string
  city: string
  state: string
  zip_code: string
}

type CustomerRow = {
  id: number
  company: string
  name: string
  person_type: string
  document: string
  email: string
  phone: string
  addresses_count: number
  addresses: CustomerAddress[]
  can_edit: boolean
  can_destroy: boolean
}

const props = defineProps<{
  customers: CustomerRow[]
  companies: Array<{ id: number; name: string }>
  query: {
    q?: string
    company_id?: string
    page: number
    per_page: number
  }
  pagination: {
    page: number
    perPage: number
    total: number
  }
  can_create: boolean
}>()

const { confirm } = useConfirmation()
const notify = useNotifications()

const columns: UiDataTableColumn[] = [
  { key: "company", label: "Empresa" },
  { key: "name", label: "Cliente" },
  { key: "document", label: "Documento" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Telefone" },
  { key: "addresses_count", label: "Enderecos", align: "center" },
  { key: "actions", label: "Acoes", align: "right" },
]

const filters: UiDataTableFilter[] = [
  {
    key: "company_id",
    label: "Empresa",
    options: props.companies.map((company) => ({ label: company.name, value: company.id })),
  },
]

const selectedFilters = reactive<Record<string, string | number | "">>({
  company_id: props.query.company_id ?? "",
})

const isDetailsDrawerOpen = ref(false)
const selectedCustomer = ref<CustomerRow | null>(null)

function openDetailsDrawer(customer: CustomerRow) {
  selectedCustomer.value = customer
  isDetailsDrawerOpen.value = true
}

function fetchWithQuery(overrides: Record<string, string | number | ""> = {}) {
  const params = {
    q: String(overrides.q ?? props.query.q ?? ""),
    company_id: String(overrides.company_id ?? selectedFilters.company_id ?? ""),
    page: Number(overrides.page ?? props.pagination.page),
    per_page: Number(overrides.per_page ?? props.pagination.perPage),
  }

  router.get("/customers", params, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
  })
}

function updateSearch(value: string) {
  fetchWithQuery({ q: value, page: 1 })
}

function updateFilter({ key, value }: { key: string; value: string | number | "" }) {
  selectedFilters[key] = value
  fetchWithQuery({ [key]: value, page: 1 })
}

function changePage(page: number) {
  fetchWithQuery({ page })
}

function changePerPage(perPage: number) {
  fetchWithQuery({ per_page: perPage, page: 1 })
}

async function destroyCustomer(id: number) {
  const ok = await confirm({
    title: "Excluir cliente",
    message: "Deseja excluir este cliente e seus enderecos?",
    type: "danger",
    confirmText: "Excluir",
  })
  if (!ok) return

  router.delete(`/customers/${id}`, {
    preserveScroll: true,
    onSuccess: () => notify.success("Cliente removido com sucesso"),
  })
}

function formatAddress(address: CustomerAddress): string {
  const line1 = [address.street, address.number].filter(Boolean).join(", ")
  const line2 = [address.district, address.city, address.state].filter(Boolean).join(" - ")
  return [line1, line2, formatZipCode(address.zip_code)].filter(Boolean).join(" | ")
}

function formatCustomerDocument(customer: CustomerRow): string {
  const digits = customer.document || ""

  if (customer.person_type === "company") return formatCnpj(digits)

  return formatCpf(digits)
}
</script>

<template>
  <AppShell>
    <div class="space-y-6">
      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h1 class="text-2xl font-semibold text-slate-900">Clientes</h1>
        <p class="mt-2 text-sm text-slate-600">Mantenha cadastro comercial e multiplos enderecos por cliente.</p>
      </section>

      <UiDataTable
        :columns="columns"
        :rows="customers"
        :search="query.q ?? ''"
        :filters="filters"
        :selected-filters="selectedFilters"
        :pagination="pagination"
        @update:search="updateSearch"
        @update:filter="updateFilter"
        @page-change="changePage"
        @per-page-change="changePerPage"
      >
        <template #toolbar-actions>
          <Link
            v-if="can_create"
            href="/customers/new"
            class="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            Novo cliente
          </Link>
        </template>

        <template #cell="{ row, column, value }">
          <div v-if="column.key === 'actions'" class="flex justify-end gap-2">
            <button
              type="button"
              class="rounded-md border border-slate-300 p-1.5 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
              aria-label="Visualizar cliente"
              @click="openDetailsDrawer(row as CustomerRow)"
            >
              <EyeIcon class="size-4" />
            </button>

            <Link
              v-if="(row as CustomerRow).can_edit"
              :href="`/customers/${(row as CustomerRow).id}/edit`"
              class="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
            >
              Editar
            </Link>

            <UiButton v-if="(row as CustomerRow).can_destroy" size="sm" variant="danger" @click="destroyCustomer((row as CustomerRow).id)">
              Excluir
            </UiButton>
          </div>

          <span v-else-if="column.key === 'document'">{{ formatCustomerDocument(row as CustomerRow) || "-" }}</span>
          <span v-else-if="column.key === 'phone'">{{ formatPhone((row as CustomerRow).phone) || "-" }}</span>
          <span v-else>{{ value || "-" }}</span>
        </template>
      </UiDataTable>

      <UiDrawer v-model="isDetailsDrawerOpen" title="Detalhes do cliente" size="2xl">
        <div v-if="selectedCustomer" class="space-y-4">
          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h3 class="text-lg font-semibold text-slate-900">{{ selectedCustomer.name }}</h3>
            <p class="mt-1 text-sm text-slate-600">Empresa: {{ selectedCustomer.company }}</p>

            <div class="mt-4 grid gap-3 md:grid-cols-3">
              <article class="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p class="text-xs uppercase tracking-wide text-slate-500">Documento</p>
                <p class="mt-1 text-sm font-medium text-slate-900">{{ formatCustomerDocument(selectedCustomer) || "-" }}</p>
              </article>
              <article class="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p class="text-xs uppercase tracking-wide text-slate-500">Email</p>
                <p class="mt-1 text-sm font-medium text-slate-900">{{ selectedCustomer.email || "-" }}</p>
              </article>
              <article class="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p class="text-xs uppercase tracking-wide text-slate-500">Telefone</p>
                <p class="mt-1 text-sm font-medium text-slate-900">{{ formatPhone(selectedCustomer.phone) || "-" }}</p>
              </article>
            </div>
          </section>

          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h4 class="mb-3 text-sm font-semibold text-slate-800">Enderecos cadastrados</h4>
            <div class="space-y-2">
              <article
                v-for="address in selectedCustomer.addresses"
                :key="address.id"
                class="rounded-md border border-slate-100 bg-slate-50 p-3"
              >
                <p class="text-xs uppercase tracking-wide text-slate-500">{{ address.label }}</p>
                <p class="mt-1 text-sm text-slate-700">{{ formatAddress(address) }}</p>
                <p v-if="address.complement" class="text-xs text-slate-500">Complemento: {{ address.complement }}</p>
              </article>

              <p v-if="selectedCustomer.addresses.length === 0" class="text-sm text-slate-500">Nenhum endereco cadastrado.</p>
            </div>
          </section>
        </div>
      </UiDrawer>
    </div>
  </AppShell>
</template>

